import { useTranslation } from 'react-i18next';
import {
  useActiveQuests,
  usePitchInQuest,
  newIdempotencyKey,
  resolveErrorMessage,
  QuestObjectiveResponseSourceKind,
  type QuestResponse,
  type QuestObjectiveResponse,
} from '@kidshome/api-contract';
import { formatPoints } from '@kidshome/i18n';
import { ProgressBar, useToast } from '@/shared/ui';
import { ConceptIcon } from '@/shared/ui/ConceptIcon';
import { questArt, conceptArt } from '@/assets/icons';
import { useFeature } from '@/entities/session';
import { useHouseholdContext } from '@/shared/lib/household';
import { tDynamic } from '@/shared/lib/i18n';
import { Feature } from '@/shared/lib/features';
import { questTypeGlyph } from '@/shared/lib/quest-types';

/**
 * KidQuestCard — the kid Home's cozy view of the SHARED family quest (the kid counterpart to the
 * command-center {@link FamilyQuestCard} on the guardian dashboard). The kid reads the same active quest
 * via `quest:read`: the headline progress bar + the cooperative "Together you've done N of M!" line, then
 * a plain-language CHECKLIST of the quest's goals so a child can actually tell WHAT TO DO. Each goal
 * shows its own name + how-far-along, a tick once it's full, and — for a cooperative `together` goal —
 * the "I helped!" tap (the only quest action a child takes; chores/habits/coins fill automatically as
 * the kid does them, a `milestone` is a grown-up's finish-line). A just-completed quest swaps the
 * checklist for a calm shared celebration.
 *
 * Secondary, no-FOMO surface (mirrors {@link HeroPresence}): hidden when `Feature.Quests` is off,
 * renders NOTHING while loading, on error, OR when there is no active quest — so a kid's day is never
 * cluttered with an empty "no quest" block. It appears only when there's a real goal to rally around.
 */
export function KidQuestCard(): React.ReactElement | null {
  const { t } = useTranslation('family');
  const { locale } = useHouseholdContext();
  const hasFeature = useFeature();
  const questsEnabled = hasFeature(Feature.Quests);
  const quests = useActiveQuests({ enabled: questsEnabled });

  if (!questsEnabled) return null;
  // A transient read failure (or the in-flight load) must never break or clutter the day — the card is
  // a secondary surface, so it simply stays absent until there's a quest to show.
  if (quests.isPending || quests.isError) return null;

  const active = quests.data?.quests ?? [];
  if (active.length === 0) return null;

  return (
    <section className="kid-quests flex flex-col gap-2">
      <h2 className="bucket-h">{t('quest.kidSectionTitle')}</h2>
      {active.map((quest) => (
        <KidQuest key={quest.id} quest={quest} locale={locale} t={t} />
      ))}
    </section>
  );
}

function KidQuest({
  quest,
  locale,
  t,
}: {
  quest: QuestResponse;
  locale: string;
  t: ReturnType<typeof useTranslation<'family'>>['t'];
}): React.ReactElement {
  // A just-completed quest stays in the `active` read for a short celebration window — show the kid a
  // calm shared "we did it!" moment with the promised real-world reward (a guardian marks it given).
  if (quest.status === 'completed') {
    return <KidQuestDone quest={quest} t={t} />;
  }

  const pct = Math.round(quest.percent);

  return (
    <div className="card quest-card">
      <div className="quest-head">
        <span className="quest-ic" aria-hidden="true">
          <ConceptIcon
            art={questArt(quest.questType)}
            glyph={questTypeGlyph(quest.questType)}
            size={26}
          />
        </span>
        <div className="quest-head-main">
          {/* Quest title is RAW user content — rendered verbatim, never translated. */}
          <p className="quest-title">{quest.title}</p>
          <p className="quest-sub">{t('quest.subtitle')}</p>
        </div>
        <span className="quest-pct">{pct}%</span>
      </div>

      <ProgressBar percent={quest.percent} label={t('quest.progressLabel')} />

      <p className="quest-together">
        {t('quest.together', {
          progress: formatPoints(locale, quest.progress),
          target: formatPoints(locale, quest.targetCount),
        })}
      </p>

      {/* The plain-language checklist of goals — the answer to "what do I need to do?". Each goal is a
          to-do row (open ring → green tick when full), with the cooperative one tappable inline. */}
      {quest.objectives.length > 0 && (
        <div className="quest-goals-wrap">
          <p className="quest-goals-label">{t('quest.kidGoalsLabel')}</p>
          <ul className="quest-goals">
            {quest.objectives.map((obj) => (
              <KidGoalRow key={obj.id} questId={quest.id} objective={obj} locale={locale} t={t} />
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

/**
 * One goal in the kid checklist: a status node (open ring while to-do, a green tick once full), the
 * goal's name, and how-far-along ("1 of 3" / "Done"), with a slim fill bar for multi-step goals. A
 * `together` goal adds the "I helped!" pitch-in tap (the only kid quest action); a `milestone` adds a
 * gentle "a grown-up will check this off" so the kid isn't left waiting on a tap they can't make.
 */
function KidGoalRow({
  questId,
  objective,
  locale,
  t,
}: {
  questId: string;
  objective: QuestObjectiveResponse;
  locale: string;
  t: ReturnType<typeof useTranslation<'family'>>['t'];
}): React.ReactElement {
  const toast = useToast();
  const { t: tErr } = useTranslation(['family', 'errors']);
  // ANY member pitches in (ADR-0049). The route is `@Idempotent`: a FRESH key per tap (distinct taps
  // each count; an offline replay of one tap never double-counts). The hook invalidates the quest views.
  const pitch = usePitchInQuest({
    onSuccess: () => toast.success(t('quest.objective.pitchedIn')),
    onError: (error) => {
      const { key, params } = resolveErrorMessage(error);
      toast.error(tDynamic(tErr, key, params));
    },
  });

  const done = objective.done;
  const isTogether = objective.sourceKind === QuestObjectiveResponseSourceKind.together;
  const isMilestone = objective.sourceKind === QuestObjectiveResponseSourceKind.milestone;
  // A fill bar + "N of M" only helps for a multi-step goal; a single-step goal reads as a plain to-do.
  const multiStep = objective.targetCount > 1;
  const pct = objective.targetCount > 0 ? (objective.progress / objective.targetCount) * 100 : 0;

  return (
    <li className={`quest-goal${done ? ' is-done' : ''}`}>
      <span className="qg-node" aria-hidden="true">
        {done ? '✓' : ''}
      </span>
      <div className="qg-body">
        <div className="qg-top">
          {/* Goal label is RAW user content — verbatim, never translated. Wraps (never truncates) so a
              child can always read the whole task. */}
          <span className="qg-label">{objective.label}</span>
          <span className="qg-count">
            {done
              ? t('quest.objective.doneLabel')
              : multiStep
                ? t('quest.objective.progress', {
                    progress: formatPoints(locale, objective.progress),
                    target: formatPoints(locale, objective.targetCount),
                  })
                : null}
          </span>
        </div>

        {!done && multiStep && (
          <span className="qg-bar" aria-hidden="true">
            <span className="qg-fill" style={{ width: `${pct}%` }} />
          </span>
        )}

        {!done && isMilestone && (
          <span className="qg-hint">
            <span aria-hidden="true">{'🌟 '}</span>
            {t('quest.objective.milestoneKidHint')}
          </span>
        )}

        {!done && isTogether && (
          <button
            type="button"
            className="occ-act warm qg-btn"
            disabled={pitch.isPending}
            onClick={() =>
              pitch.mutate({
                questId,
                objectiveId: objective.id,
                idempotencyKey: newIdempotencyKey(),
              })
            }
          >
            <span aria-hidden="true">{'🙌 '}</span>
            {t('quest.objective.pitchIn')}
          </button>
        )}
      </div>
    </li>
  );
}

/** The kid's calm completion moment — a 🎉, the quest name, "we did it!", and the promised IRL reward. */
function KidQuestDone({
  quest,
  t,
}: {
  quest: QuestResponse;
  t: ReturnType<typeof useTranslation<'family'>>['t'];
}): React.ReactElement {
  return (
    <div className="card quest-card quest-done" role="status">
      <ConceptIcon art={conceptArt('celebrate')} glyph="🎉" size={40} />
      {/* Quest title + reward note are RAW user content — verbatim. */}
      <p className="quest-title quest-done-title">{quest.title}</p>
      <p className="quest-done-line">{t('quest.celebrate.weDidIt')}</p>
      {quest.rewardNote && <p className="quest-reward">{quest.rewardNote}</p>}
    </div>
  );
}
